import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FiX } from 'react-icons/fi';
import styled, { createGlobalStyle } from 'styled-components';
import Swal from 'sweetalert2';
import { assistantApi } from '../../services/assistant';
import { useTheme } from '../../theme';
import type { ThemeConfig } from '../../theme';
import type { TokenPlan } from '../../types/tokenPlans';

interface TokenPlansModalProps {
  show: boolean;
  onClose: () => void;
}

export const TokenPlansModal: React.FC<TokenPlansModalProps> = ({ show, onClose }) => {
  const theme = useTheme();
  const [plans, setPlans] = useState<TokenPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchasingPlanId, setPurchasingPlanId] = useState<number | null>(null);

  useEffect(() => {
    if (show) {
      fetchPlans();
    }
  }, [show]);

  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await assistantApi.getTokenPlans();
      setPlans(response.plans || []);
    } catch (err: any) {
      console.error('Error fetching token plans:', err);
      setError('Failed to load token plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (planId: number) => {
    setPurchasingPlanId(planId);
    try {
      const response = await assistantApi.initiateTokenPurchase(planId);
      if (response.success && response.payment_url) {
        // Open payment page in new tab
        window.open(response.payment_url, '_blank');
        onClose();
      } else {
        Swal.fire({
          title: 'Purchase Failed',
          text: response.error || 'Failed to initiate purchase. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } catch (err: any) {
      console.error('Error initiating purchase:', err);
      Swal.fire({
        title: 'Purchase Failed',
        text: 'An error occurred while processing your request. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setPurchasingPlanId(null);
    }
  };

  const formatTokenCount = (count: number): string => {
    if (count >= 1000000) {
      const millions = count / 1000000;
      return `${millions % 1 === 0 ? millions : millions.toFixed(1)}M`;
    }
    return count.toLocaleString();
  };

  return (
    <>
      <ModalStyles />
      <Modal
        show={show}
        onHide={onClose}
        dialogClassName="token-plans-modal"
        contentClassName="transparent-modal-content"
        centered
      >
        <StyledModalContent>
          <CustomCloseButton onClick={onClose}>
            <FiX />
          </CustomCloseButton>
          <ContentCard $theme={theme}>
            <ModalHeader $theme={theme}>
              <h2 className="modal-title">Buy Token Packs</h2>
              <p className="modal-subtitle">
                Purchase additional tokens to continue using AskVantage
              </p>
            </ModalHeader>
            <ModalContent $theme={theme}>
              {loading && (
                <LoadingState $theme={theme}>
                  <Spinner />
                  <p>Loading token plans...</p>
                </LoadingState>
              )}

              {error && (
                <ErrorState $theme={theme}>
                  <p>{error}</p>
                  <RetryButton onClick={fetchPlans} $theme={theme}>
                    Retry
                  </RetryButton>
                </ErrorState>
              )}

              {!loading && !error && plans.length === 0 && (
                <EmptyState $theme={theme}>
                  <p>No token plans available at the moment.</p>
                </EmptyState>
              )}

              {!loading && !error && plans.length > 0 && (
                <PlansGrid>
                  {plans.map((plan) => (
                    <PlanCard key={plan.id} $theme={theme}>
                      <PlanName $theme={theme}>{plan.name}</PlanName>
                      <TokenCount $theme={theme}>
                        {formatTokenCount(plan.token_count)} tokens
                      </TokenCount>
                      <PlanPrice $theme={theme}>
                        {parseFloat(String(plan.price)).toFixed(2)} {plan.currency}
                      </PlanPrice>
                      {plan.description && (
                        <PlanDescription $theme={theme}>{plan.description}</PlanDescription>
                      )}
                      <BuyButton
                        onClick={() => handlePurchase(plan.id)}
                        disabled={purchasingPlanId === plan.id}
                        $theme={theme}
                        $isProcessing={purchasingPlanId === plan.id}
                      >
                        {purchasingPlanId === plan.id ? 'Processing...' : 'Buy Now'}
                      </BuyButton>
                    </PlanCard>
                  ))}
                </PlansGrid>
              )}
            </ModalContent>
          </ContentCard>
        </StyledModalContent>
      </Modal>
    </>
  );
};

const ModalStyles = createGlobalStyle`
  .token-plans-modal {
    max-width: 700px !important;
  }
  .token-plans-modal .transparent-modal-content {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  .token-plans-modal .modal-content {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }
`;

const StyledModalContent = styled.div`
  position: relative;
  background: transparent;
  padding: 20px;
`;

const ContentCard = styled.div<{ $theme: ThemeConfig }>`
  background: ${(props) => props.$theme.colors.messageBoxBg};
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  position: relative;
  border: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div<{ $theme: ThemeConfig }>`
  padding: 32px 32px 16px 32px;
  border-bottom: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};

  .modal-title {
    margin: 0 0 8px 0;
    font-size: 24px;
    font-weight: 700;
    color: ${(props) => props.$theme.colors.mainText};
  }

  .modal-subtitle {
    margin: 0;
    font-size: 14px;
    color: ${(props) => props.$theme.colors.mainText};
    opacity: 0.7;
  }
`;

const CustomCloseButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #6b7280;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }

  &:hover {
    background: #4b5563;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalContent = styled.div<{ $theme: ThemeConfig }>`
  padding: 24px 32px 32px 32px;
  color: ${(props) => props.$theme.colors.mainText};
`;

const LoadingState = styled.div<{ $theme: ThemeConfig }>`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => props.$theme.colors.mainText};

  p {
    margin-top: 16px;
    font-size: 14px;
    opacity: 0.7;
  }
`;

const Spinner = styled.div`
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 3px solid rgba(110, 190, 127, 0.2);
  border-top-color: #6ebe7f;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorState = styled.div<{ $theme: ThemeConfig }>`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => props.$theme.colors.mainText};

  p {
    margin-bottom: 16px;
    font-size: 14px;
    color: #ef4444;
  }
`;

const RetryButton = styled.button<{ $theme: ThemeConfig }>`
  background: linear-gradient(135deg, #6ebe7f 0%, #4a9960 100%);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(110, 190, 127, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const EmptyState = styled.div<{ $theme: ThemeConfig }>`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => props.$theme.colors.mainText};
  opacity: 0.7;
  font-size: 14px;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const PlanCard = styled.div<{ $theme: ThemeConfig }>`
  background: ${(props) =>
    props.$theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid ${(props) => props.$theme.colors.messageBoxBorder};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #6ebe7f;
  }
`;

const PlanName = styled.h3<{ $theme: ThemeConfig }>`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.$theme.colors.mainText};
`;

const TokenCount = styled.div<{ $theme: ThemeConfig }>`
  font-size: 28px;
  font-weight: 700;
  color: #6ebe7f;
  margin-bottom: 8px;
`;

const PlanPrice = styled.div<{ $theme: ThemeConfig }>`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => props.$theme.colors.mainText};
  margin-bottom: 16px;
`;

const PlanDescription = styled.p<{ $theme: ThemeConfig }>`
  font-size: 13px;
  color: ${(props) => props.$theme.colors.mainText};
  opacity: 0.7;
  margin-bottom: 20px;
  flex-grow: 1;
  line-height: 1.5;
`;

const BuyButton = styled.button<{
  $theme: ThemeConfig;
  $isProcessing: boolean;
}>`
  background: linear-gradient(135deg, #6ebe7f 0%, #4a9960 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(110, 190, 127, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${(props) =>
    props.$isProcessing &&
    `
    &::after {
      content: '';
      display: inline-block;
      width: 12px;
      height: 12px;
      margin-left: 8px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }
  `}

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default TokenPlansModal;
